import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, Eye, FileCheck, AlertTriangle,
  KeyRound, Scan, Server, Globe, CheckCircle, Users
} from "lucide-react";

const cyberServices = [
  {
    icon: ShieldCheck,
    title: "Cybersecurity Strategy & Assessment",
    items: [
      "Enterprise-wide security posture assessment and gap analysis",
      "Tailored cybersecurity roadmaps aligned to business objectives",
      "Threat landscape evaluation and risk scoring",
      "Security maturity model benchmarking (NIST, DFARS, ISO 27001)",
    ],
  },
  {
    icon: Lock,
    title: "Data Protection & Privacy",
    items: [
      "Data classification, encryption, and access governance",
      "Privacy impact assessments (PIA) and data flow mapping",
      "GDPR, CCPA, and regional data protection compliance",
      "Data loss prevention (DLP) strategy and implementation",
    ],
  },
  {
    icon: Eye,
    title: "Security Operations & Monitoring",
    items: [
      "24x7 Security Operations Center (SOC) support",
      "SIEM deployment, tuning, and managed detection & response",
      "Real-time threat intelligence and incident response",
      "Intrusion detection/prevention (IDS/IPS) management",
    ],
  },
  {
    icon: FileCheck,
    title: "Regulatory Compliance & Audit Readiness",
    items: [
      "Compliance frameworks: SOX, HIPAA, SOC 2, ISO 27001, PCI DSS",
      "Audit preparation, evidence collection, and remediation",
      "Continuous compliance monitoring and automated reporting",
      "Policy development and regulatory change management",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Risk Management & Incident Response",
    items: [
      "Enterprise risk assessment and mitigation planning",
      "Business continuity and disaster recovery (BC/DR) planning",
      "Incident response playbooks and tabletop exercises",
      "Third-party and vendor risk management programs",
    ],
  },
  {
    icon: KeyRound,
    title: "Identity & Access Management (IAM)",
    items: [
      "Role-based access control (RBAC) design and governance",
      "Privileged access management (PAM) implementation",
      "Multi-factor authentication (MFA) and SSO integration",
      "Identity lifecycle management aligned to ERP governance",
    ],
  },
];

const complianceFrameworks = [
  { name: "ISO 27001", desc: "Information Security Management System" },
  { name: "ISO 27701", desc: "Privacy Information Management System" },
  { name: "ISO 20000", desc: "IT Service Management" },
  { name: "ISO 27017", desc: "Information Security for Cloud Services" },
  { name: "ISO 22301", desc: "Business Continuity Management System" },
  { name: "ISO 27018", desc: "Personally Identifiable Information (PII) Public Clouds" },
  { name: "ISO 42001", desc: "AI Management System Compliance" },
];

const benefits = [
  { icon: ShieldCheck, text: "Proactive threat detection and reduced breach risk" },
  { icon: FileCheck, text: "Continuous audit readiness across all regulatory frameworks" },
  { icon: Globe, text: "Global compliance coverage for multi-country operations" },
  { icon: Server, text: "Integrated security across on-prem, cloud, and hybrid environments" },
  { icon: Users, text: "Security awareness training and governance culture building" },
  { icon: CheckCircle, text: "Measurable reduction in security incidents and compliance gaps" },
];

const deliveryApproach = [
  { step: "01", icon: Scan, title: "Assess & Discover", desc: "Comprehensive security assessment, vulnerability scanning, and compliance gap analysis across your IT landscape." },
  { step: "02", icon: ShieldCheck, title: "Design & Harden", desc: "Develop security architecture, implement controls, and harden systems against identified threats and vulnerabilities." },
  { step: "03", icon: Eye, title: "Monitor & Detect", desc: "Deploy continuous monitoring, threat detection, and real-time alerting with 24x7 SOC operations." },
  { step: "04", icon: AlertTriangle, title: "Respond & Recover", desc: "Rapid incident response, containment, forensics, and recovery with documented playbooks and lessons learned." },
];

export default function CyberServicePage() {
  return (
    <div data-testid="cyber-service-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
                Cybersecurity & Compliance
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
                Safeguard Your Enterprise, Ensure Compliance
              </h1>
              <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-8">
                In an era of escalating cyber threats and evolving regulations, TEQXL delivers
                end-to-end cybersecurity and compliance services that protect your data, secure
                your operations, and ensure audit readiness — so you can focus on growth with
                confidence.
              </p>
              <Link
                to="/contact"
                data-testid="cyber-hero-cta"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
              >
                Request a Security Assessment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="overflow-hidden lg:overflow-visible">
              <img
                src="/cyber-compliance.png"
                alt="Cybersecurity & Compliance"
                className="w-full h-auto lg:h-[400px] lg:object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section data-testid="cyber-services-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              What We Deliver
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Comprehensive cybersecurity & compliance services
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200 bg-white">
            {cyberServices.map((service, i) => (
              <div
                key={service.title}
                data-testid={`cyber-service-item-${i}`}
                className={`p-8 lg:p-10 border-b border-gray-200 last:border-b-0 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""}`}
              >
                <service.icon className="w-6 h-6 text-[#002FA7] mb-5" />
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-4">
                  {service.title}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Approach */}
      <section data-testid="cyber-delivery-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Delivery Approach
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              End-to-end security lifecycle
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200">
            {deliveryApproach.map((phase, i) => (
              <div
                key={phase.title}
                data-testid={`cyber-phase-${i}`}
                className={`p-8 lg:p-10 ${i < 3 ? "lg:border-r border-gray-200" : ""} ${
                  i < 2 ? "sm:border-b lg:border-b-0 border-gray-200" : ""
                } border-b lg:border-b-0 border-gray-200 last:border-b-0`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-heading text-2xl font-bold text-[#002FA7] opacity-30">
                    {phase.step}
                  </span>
                  <phase.icon className="w-5 h-5 text-[#002FA7]" />
                </div>
                <h4 className="text-sm font-semibold text-[#0A0A0A] mb-3">{phase.title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Frameworks */}
      <section data-testid="compliance-frameworks-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                ISO Compliance
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Frameworks we support
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Our compliance specialists bring deep expertise across global and industry-specific
                ISO frameworks, ensuring your organization meets and exceeds compliance
                requirements with confidence.
              </p>
            </div>
            <div className="border border-gray-200 bg-white">
              {complianceFrameworks.map((fw, i) => (
                <div
                  key={fw.name}
                  data-testid={`compliance-framework-${i}`}
                  className={`flex items-start gap-4 p-5 ${
                    i < complianceFrameworks.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <p className="font-heading text-base font-bold text-[#002FA7] shrink-0 w-24">{fw.name}</p>
                  <p className="text-sm text-[#4B5563]">{fw.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="cyber-benefits-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Business Benefits
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Why TEQXL for cybersecurity & compliance
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200">
            {benefits.map((b, i) => (
              <div
                key={b.text}
                data-testid={`cyber-benefit-${i}`}
                className={`flex items-start gap-4 p-6 lg:p-8 border-b border-gray-200 last:border-b-0 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""}`}
              >
                <b.icon className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                <p className="text-sm text-[#0A0A0A]">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Protect your enterprise. Ensure compliance.
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            Don't wait for a breach or a failed audit. Partner with TEQXL to build
            a resilient security posture and maintain continuous compliance.
          </p>
          <Link
            to="/contact"
            data-testid="cyber-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Schedule a Security Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
